import { TbCheck, TbCopy, TbDownload, TbPencil, TbX } from "solid-icons/tb";
import { type Component, createSignal, onCleanup, onMount, Show } from "solid-js";
import { Portal } from "solid-js/web";
import { AddToCanvasMenu, type CanvasTarget } from "./AddToCanvasMenu";

interface MediaLightboxProps {
  src: string;
  alt?: string;
  /** True while the copy control should read as confirmed; owned by the caller so both surfaces agree. */
  copied?: boolean;
  /** Control labels; the caller supplies translated ones where it has them. */
  copyLabel?: string;
  copiedLabel?: string;
  downloadLabel?: string;
  closeLabel?: string;
  onCopy?: () => void;
  onDownload?: () => void;
  /**
   * Opens the image for markup. Only a surface that has a markup editor supplies
   * this; the web live-session page renders the same overlay without one.
   */
  onAnnotate?: () => void;
  /** Same pair as TranscriptMedia: canvases to offer, and the placement. */
  canvasTargets?: CanvasTarget[];
  onAddToCanvas?: (targetId?: string) => void | Promise<void>;
  onClose: () => void;
}

// Rendered only while open, so the browser-only work (focus, document listener)
// can live in onMount without an isServer guard on the server render path.
export const MediaLightbox: Component<MediaLightboxProps> = (props) => {
  let closeRef: HTMLButtonElement | undefined;
  let barRef: HTMLDivElement | undefined;
  // The canvas menu owns Escape and Tab while it is up; this handler runs at
  // document capture and would otherwise close the whole preview under it.
  const [menuOpen, setMenuOpen] = createSignal(false);

  onMount(() => {
    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (menuOpen()) return;
      if (event.key === "Escape") {
        // Captured at the document so the topmost layer consumes Escape: other
        // surfaces that dismiss on Escape (highlight bubble, visual editor)
        // listen in the bubble phase and never see it while the overlay is up.
        event.stopPropagation();
        props.onClose();
        return;
      }
      // Cycling Tab through the overlay's own controls is the whole focus trap:
      // nothing behind the backdrop may take focus while it is up.
      if (event.key === "Tab") {
        event.preventDefault();
        const controls = Array.from(barRef?.querySelectorAll("button") ?? []);
        // Clicking the image or backdrop leaves focus on <body>, so indexOf
        // returns -1; enter the cycle at the end rather than computing from it.
        const at = controls.indexOf(document.activeElement as HTMLButtonElement);
        const step = event.shiftKey ? -1 : 1;
        const next = at < 0 ? controls.length - 1 : (at + step + controls.length) % controls.length;
        controls[next]?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    onCleanup(() => {
      document.removeEventListener("keydown", onKeyDown, true);
      restoreTo?.focus();
    });
  });

  const onBackdropClick = (event: MouseEvent) => {
    if (event.target === event.currentTarget) props.onClose();
  };

  return (
    <Portal>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={props.alt || "Image preview"}
        onClick={onBackdropClick}
        class="fixed inset-0 z-100 flex cursor-zoom-out flex-col items-center justify-center gap-3 bg-black/80 p-6"
      >
        <img
          src={props.src}
          alt={props.alt ?? ""}
          class="max-h-[80vh] min-h-0 max-w-[90vw] cursor-default rounded-lg object-contain shadow-2xl"
        />
        {/* Under the image rather than floating over a corner of it: on the
            backdrop the controls read as window chrome, and what can be done
            with the image has to be discovered by hovering it. */}
        <div ref={barRef} class="flex shrink-0 items-center gap-1 rounded-full bg-white/10 p-1">
          <Show when={props.onCopy}>
            <button
              type="button"
              aria-label={props.copied ? (props.copiedLabel ?? "Copied") : (props.copyLabel ?? "Copy image")}
              onClick={() => props.onCopy?.()}
              class="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white hover:bg-white/15 focus:ring-2 focus:ring-brand focus:outline-none"
            >
              <Show when={props.copied} fallback={<TbCopy class="size-5" />}>
                <TbCheck class="size-5" />
              </Show>
              {props.copied ? (props.copiedLabel ?? "Copied") : (props.copyLabel ?? "Copy image")}
            </button>
          </Show>
          <Show when={props.onDownload}>
            <button
              type="button"
              aria-label={props.downloadLabel ?? "Download image"}
              onClick={() => props.onDownload?.()}
              class="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white hover:bg-white/15 focus:ring-2 focus:ring-brand focus:outline-none"
            >
              <TbDownload class="size-5" />
              {props.downloadLabel ?? "Download image"}
            </button>
          </Show>
          <Show when={props.onAddToCanvas && props.canvasTargets}>
            <AddToCanvasMenu
              targets={props.canvasTargets ?? []}
              onAdd={(id) => props.onAddToCanvas?.(id)}
              showLabel
              triggerClass="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-brand"
              contentClass="z-101"
              onOpenChange={setMenuOpen}
            />
          </Show>
          <Show when={props.onAnnotate}>
            <button
              type="button"
              aria-label="Mark up image"
              onClick={() => props.onAnnotate?.()}
              class="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white hover:bg-white/15 focus:ring-2 focus:ring-brand focus:outline-none"
            >
              <TbPencil class="size-5" />
              Mark up image
            </button>
          </Show>
          <button
            ref={closeRef}
            type="button"
            aria-label={props.closeLabel ?? "Close image preview"}
            onClick={() => props.onClose()}
            class="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-white hover:bg-white/15 focus:ring-2 focus:ring-brand focus:outline-none"
          >
            <TbX class="size-5" />
            {props.closeLabel ?? "Close"}
          </button>
        </div>
      </div>
    </Portal>
  );
};
